// ExitIntentPopup — Shows a configurable popup when user tries to leave the booking page
import { useState, useEffect, useRef, useCallback } from "react";
import { X, Gift, Clock, ArrowRight, Sparkle, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { renderMarkdown } from "@/lib/markdown";
import { stripDealCode } from "@/lib/sanitizeDealCode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExitIntentPopup({ variantId }) {
  const [config, setConfig] = useState(null);
  const [accentColor, setAccentColor] = useState("#00A09B");
  const [leadForm, setLeadForm] = useState(null); // shared lead-form config (source of truth for the deal code)
  const [expires, setExpires] = useState({ state: "active", display: null, hidden: false });
  const [termsMd, setTermsMd] = useState(""); // variant's details_md — drives the "View terms" link
  const [showTerms, setShowTerms] = useState(false);
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "" });
  const [smsConsent, setSmsConsent] = useState(false);
  // P0 Feb 2026 — registered visitors must not see exit-intent again.
  // Read from /api/visitors/me which returns claimed_codes scoped to
  // this campaign. We block the popup entirely when the visitor has
  // already claimed a code in this campaign.
  const [alreadyRegistered, setAlreadyRegistered] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [visitorClaims, setVisitorClaims] = useState(null);
  // Phase 1.4 — single combined TCPA checkbox. UI shows one consent box;
  // marketing_opt_in moves in lockstep with sms_consent so segmentation
  // continues to work without forcing the visitor to read two checkboxes.
  const marketingOptIn = smsConsent;
  const setMarketingOptIn = setSmsConsent;
  const [countdown, setCountdown] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // { code_sent, message }
  const [submitting, setSubmitting] = useState(false);
  const [tracked, setTracked] = useState(false);
  const timerRef = useRef(null);
  const triggerRef = useRef(false);
  // Bypass Emergent visual-editor babel transform that strips inline
  // `style={{...}}` props (x-dynamic="false"). We set the dynamic gradient
  // background and accent color imperatively after render so the CTA
  // and accent-tinted decorations stay visible.
  const ctaBtnRef = useRef(null);
  const headerRef = useRef(null);
  useEffect(() => {
    const grad = `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`;
    if (ctaBtnRef.current) ctaBtnRef.current.style.background = grad;
    if (headerRef.current) headerRef.current.style.background = grad;
  }, [accentColor]);

  // Fetch exit-intent config + the SHARED lead-form config (single source of
  // truth for the deal code). Exit-intent is now an alternative surface for
  // the same code, with the same TCPA-compliant consent flow.
  useEffect(() => {
    if (!variantId) return;
    fetch(`${BACKEND_URL}/api/landing-variants/${variantId}/exit-intent`)
      .then((r) => r.json())
      .then((d) => {
        if (d.exit_intent?.enabled) setConfig(d.exit_intent);
        // Legacy GPT-imported variants sometimes stored accent_color as a
        // boolean — coerce to the default to keep the gradient CTA visible.
        if (typeof d.accent_color === "string" && d.accent_color.startsWith("#")) {
          setAccentColor(d.accent_color);
        }
        if (typeof d.campaign_id === "string") setCampaignId(d.campaign_id);
        // The variant's terms (`details_md`) drives the optional "View full
        // terms" link rendered in the success/decline confirmation.
        if (typeof d.details_md === "string") setTermsMd(d.details_md);
        // Canonical expiration state — single source of truth shared with
        // the lead-form. We hide the popup entirely once past grace.
        if (d.expires) setExpires(d.expires);
      })
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/landing-leads/public/${variantId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setLeadForm(d))
      .catch(() => {});

    // P0 — registered-visitor suppression. /api/visitors/me returns
    // claimed_codes (without the code itself). Cache the claims; the
    // suppression decision is computed in a separate effect once we
    // also know this variant's campaign_id (campaign-wide block).
    fetch(`${BACKEND_URL}/api/visitors/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setVisitorClaims(data?.claimed_codes || []);
      })
      .catch(() => setVisitorClaims([]));
  }, [variantId]);

  // Campaign-wide suppression: block the popup if the visitor has
  // claimed a code on ANY variant in this campaign. Falls back to
  // variant_id matching when campaign_id is missing for any reason.
  useEffect(() => {
    if (visitorClaims === null) return;
    if (visitorClaims.length === 0) {
      setAlreadyRegistered(false);
      return;
    }
    const matched = visitorClaims.some((c) =>
      campaignId ? c.campaign_id === campaignId : c.variant_id === variantId
    );
    setAlreadyRegistered(matched);
  }, [visitorClaims, campaignId, variantId]);

  // Check frequency — supports both new (count+period) and legacy (string) formats
  const canShow = useCallback(() => {
    if (!config) return false;
    // P0 — never show to a visitor who's already claimed a code on
    // this variant. We wait for the lookup to resolve (alreadyRegistered
    // === null means in-flight) so we don't fire on stale state.
    if (alreadyRegistered === null) return false;
    if (alreadyRegistered) return false;
    const key = `exit_intent_${variantId}`;

    // Parse frequency config (backward compat with old "once_per_session" strings)
    let maxCount = config.frequency_count || 1;
    let period = config.frequency_period || "session";
    if (config.frequency && !config.frequency_period) {
      // Legacy format: "once_per_session" → count=1, period="session"
      if (config.frequency === "once_per_session") { maxCount = 1; period = "session"; }
      else if (config.frequency === "once_per_day") { maxCount = 1; period = "day"; }
      else if (config.frequency === "once_ever") { maxCount = 1; period = "ever"; }
    }

    const today = new Date().toISOString().split("T")[0];

    if (period === "ever") {
      const stored = localStorage.getItem(key);
      if (!stored) return true;
      const data = JSON.parse(stored);
      return (data.count || 0) < maxCount;
    }

    if (period === "day") {
      const stored = localStorage.getItem(key);
      if (!stored) return true;
      const data = JSON.parse(stored);
      if (data.date !== today) return true; // new day, reset
      return (data.count || 0) < maxCount;
    }

    // period === "session"
    const stored = sessionStorage.getItem(key);
    if (!stored) return true;
    const data = JSON.parse(stored);
    return (data.count || 0) < maxCount;
  }, [config, variantId, alreadyRegistered]);

  // Mark as shown — increments count
  const markShown = useCallback(() => {
    const key = `exit_intent_${variantId}`;
    const today = new Date().toISOString().split("T")[0];

    // Parse period
    let period = config?.frequency_period || "session";
    if (config?.frequency && !config?.frequency_period) {
      if (config.frequency === "once_per_session") period = "session";
      else if (config.frequency === "once_per_day") period = "day";
      else if (config.frequency === "once_ever") period = "ever";
    }

    if (period === "session") {
      const stored = sessionStorage.getItem(key);
      const data = stored ? JSON.parse(stored) : { count: 0 };
      data.count = (data.count || 0) + 1;
      sessionStorage.setItem(key, JSON.stringify(data));
    }

    if (period === "day") {
      const stored = localStorage.getItem(key);
      const data = stored ? JSON.parse(stored) : { date: today, count: 0 };
      if (data.date !== today) { data.date = today; data.count = 0; }
      data.count = (data.count || 0) + 1;
      localStorage.setItem(key, JSON.stringify(data));
    }

    if (period === "ever") {
      const stored = localStorage.getItem(key);
      const data = stored ? JSON.parse(stored) : { count: 0 };
      data.count = (data.count || 0) + 1;
      localStorage.setItem(key, JSON.stringify(data));
    }
  }, [variantId, config]);

  // Track event
  const trackEvent = useCallback(
    async (eventType, couponCode = null) => {
      try {
        await fetch(`${BACKEND_URL}/api/landing-variants/exit-intent/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            variant_id: variantId,
            event_type: eventType,
            coupon_code: couponCode,
            visitor_id: sessionStorage.getItem("visitor_id") || null,
          }),
        });
      } catch (e) {
        /* silent */
      }
    },
    [variantId]
  );

  // Show popup
  const showPopup = useCallback(() => {
    if (triggerRef.current || !canShow()) return;
    triggerRef.current = true;
    markShown();
    setVisible(true);
    if (config?.countdown_enabled && ((config?.countdown_minutes || 0) > 0 || (config?.countdown_seconds || 0) > 0)) {
      setCountdown((config.countdown_minutes || 0) * 60 + (config.countdown_seconds || 0));
    }
    trackEvent("impression");
    setTracked(true);
  }, [config, canShow, markShown, trackEvent]);

  // Setup triggers
  useEffect(() => {
    if (!config) return;

    const delay = (config.delay_seconds || 0) * 1000;
    const trigger = config.trigger || "smart_exit";
    const fire = () => (delay > 0 ? setTimeout(showPopup, delay) : showPopup());

    // ── smart_exit: composite "is this person leaving?" detector ──
    // Fires on the FIRST of: mouse-leaves-top, tab hidden, idle N seconds,
    // rapid scroll-up after >50% depth, or back-button intent. Once-per-session
    // by default (controlled by frequency_period); the popup itself
    // self-throttles via canShow().
    if (trigger === "smart_exit") {
      const idleSec = config.idle_seconds || 30;
      const deepScrollPct = config.scroll_percentage || 50;
      let idleTimer;
      let lastScrollY = window.scrollY || 0;
      let hitDeepScroll = false;
      const isMobile =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;

      const resetIdle = () => {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(fire, idleSec * 1000);
      };
      ["mousemove", "keydown", "scroll", "touchstart"].forEach((ev) =>
        document.addEventListener(ev, resetIdle, { passive: true })
      );
      resetIdle();

      // Desktop only: cursor leaves through the top of the viewport.
      const onMouseOut = (e) => {
        if (!isMobile && e.clientY <= 5) fire();
      };
      document.addEventListener("mouseout", onMouseOut);

      // Tab/app backgrounded — applies to all devices.
      const onVisibility = () => {
        if (document.hidden) fire();
      };
      document.addEventListener("visibilitychange", onVisibility);

      // Rapid scroll-up after a deep scroll (mobile back-toward-back-button).
      const onScroll = () => {
        const y = window.scrollY || 0;
        const docH = (document.body.scrollHeight || 0) - window.innerHeight;
        const pct = docH > 0 ? (y / docH) * 100 : 0;
        if (pct >= deepScrollPct) hitDeepScroll = true;
        if (hitDeepScroll && lastScrollY - y > 300) fire();
        lastScrollY = y;
      };
      window.addEventListener("scroll", onScroll, { passive: true });

      // Back-button intent. Push a sentinel state so the first back press
      // fires our popup instead of leaving the page.
      try {
        history.pushState({ exitGuard: true }, "");
      } catch {
        /* ignore — non-browser env */
      }
      const onPopState = () => fire();
      window.addEventListener("popstate", onPopState);

      return () => {
        clearTimeout(idleTimer);
        ["mousemove", "keydown", "scroll", "touchstart"].forEach((ev) =>
          document.removeEventListener(ev, resetIdle)
        );
        document.removeEventListener("mouseout", onMouseOut);
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("popstate", onPopState);
      };
    }

    if (trigger === "mouse_leave") {
      const handler = (e) => { if (e.clientY <= 5) fire(); };
      document.addEventListener("mouseout", handler);
      return () => document.removeEventListener("mouseout", handler);
    }

    if (trigger === "idle") {
      let timeout;
      const resetIdle = () => {
        clearTimeout(timeout);
        timeout = setTimeout(showPopup, (config.idle_seconds || 30) * 1000);
      };
      ["mousemove", "keydown", "scroll", "touchstart"].forEach((e) => document.addEventListener(e, resetIdle));
      resetIdle();
      return () => {
        clearTimeout(timeout);
        ["mousemove", "keydown", "scroll", "touchstart"].forEach((e) => document.removeEventListener(e, resetIdle));
      };
    }

    if (trigger === "scroll") {
      const handler = () => {
        const scrollPct = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        if (scrollPct >= (config.scroll_percentage || 50)) fire();
      };
      window.addEventListener("scroll", handler);
      return () => window.removeEventListener("scroll", handler);
    }

    if (trigger === "tab_switch") {
      const handler = () => { if (document.hidden) fire(); };
      document.addEventListener("visibilitychange", handler);
      return () => document.removeEventListener("visibilitychange", handler);
    }
  }, [config, showPopup]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    timerRef.current = setInterval(() => {
      setCountdown((p) => {
        if (p <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [countdown > 0]);

  const handleDismiss = () => {
    setVisible(false);
    if (!submitted) trackEvent("dismissal");
    clearInterval(timerRef.current);
    // Allow re-triggering if frequency permits more shows
    triggerRef.current = false;
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim() || !formData.phone?.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      const utm = (k) => {
        try {
          return new URLSearchParams(window.location.search).get(k) || null;
        } catch { return null; }
      };
      const resp = await fetch(`${BACKEND_URL}/api/landing-leads/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variant_id: variantId,
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: (formData.email || "").trim() || null,
          sms_consent: !!smsConsent,
          marketing_opt_in: !!marketingOptIn,
          source: "exit_intent",
          utm_source: utm("utm_source"),
          utm_medium: utm("utm_medium"),
          utm_campaign: utm("utm_campaign") || utm("campaign"),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data?.detail || "Something went wrong");
        return;
      }
      // Mirror prior behavior: stash the lead in sessionStorage so the
      // booking flow can pre-fill name/phone/email and apply the same code.
      try {
        sessionStorage.setItem("exit_intent_coupon", JSON.stringify({
          code: leadForm?.deal_code || config?.coupon_code || "",
          deal_type: config?.deal_type,
          deal_value: config?.deal_value,
          deal_description: config?.deal_description,
          deal_applicable_services: config?.deal_applicable_services || [],
          customer_name: formData.name,
          customer_phone: formData.phone,
          customer_email: formData.email,
          consented: !!smsConsent,
        }));
      } catch { /* ignore */ }

      setSubmitResult(data);
      setSubmitted(true);
      // Track conversion (kept for legacy analytics)
      if (smsConsent) {
        trackEvent("conversion", leadForm?.deal_code || config?.coupon_code || null);
      } else {
        trackEvent("declined_consent");
      }
      // Don't auto-close — let the customer read the success/decline message.
    } catch {
      toast.error("Network error — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!visible || !config) return null;
  // Single source of truth: the canonical lead-form expiration. Hide the
  // popup entirely once we're past the grace window.
  if (expires.hidden) return null;
  const isExpiredGrace = expires.state === "expired_grace";

  // Defensive scrub of body text + token interpolation. The backend already
  // performs both, but if an older client is talking to a newer server we
  // do it again here so a stale payload doesn't leak the code.
  const safeBody = stripDealCode(
    config.body_text,
    leadForm?.deal_code || config?.coupon_code
  );

  const dealBadge =
    config.deal_type === "percentage"
      ? `${config.deal_value}% OFF`
      : config.deal_type === "fixed"
      ? `$${config.deal_value} OFF`
      : "FREE";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" data-testid="exit-intent-overlay">
      <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" data-testid="exit-intent-popup">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center transition-colors"
          data-testid="exit-intent-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Deal Badge */}
        <div ref={headerRef} className="px-6 py-4 text-white text-center" style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}>
          <div className="flex items-center justify-center gap-2 mb-1">
            <Gift className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wider">Special Offer</span>
          </div>
          <p className="text-3xl font-bold tracking-tight" data-testid="exit-intent-deal">{dealBadge}</p>
          <p className="text-sm text-white/90 mt-1">{config.deal_description}</p>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-4" data-testid="exit-intent-success">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${accentColor}1a` }}>
                <Sparkle className="w-8 h-8" style={{ color: accentColor }} />
              </div>
              <h3 className="text-xl font-bold mb-1" style={{ color: accentColor }}>
                {submitResult?.code_sent ? "Sent!" : "Thanks for your interest"}
              </h3>
              <p className="text-sm text-gray-600 px-2">
                {submitResult?.message || (submitResult?.code_sent
                  ? "Check your phone — your deal code is on its way."
                  : "Thank you for your interest. We will not message you.")}
              </p>
              {termsMd && (
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  className="mt-3 text-xs text-gray-500 underline hover:text-gray-700"
                  data-testid="exit-intent-view-terms"
                >
                  View full terms and conditions
                </button>
              )}
              <div>
                <button
                  onClick={handleDismiss}
                  className="mt-3 text-xs text-gray-400 hover:text-gray-600 underline"
                  data-testid="exit-intent-success-close"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              <h3 className="text-xl font-bold text-gray-900 mb-1 text-center" data-testid="exit-intent-headline">
                {config.headline}
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">{safeBody}</p>

              {/* Auto-pill for active deals — visible "Offer expires …" line.
                  Always rendered when the owner has set an expiration so the
                  customer sees the urgency even when no countdown is enabled. */}
              {expires.display && expires.state === "active" && (
                <div
                  className="flex items-center justify-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[11px] font-semibold text-amber-800 mx-auto w-fit"
                  data-testid="exit-intent-expires-pill"
                >
                  <Clock className="w-3 h-3" />
                  Offer expires {expires.display}
                </div>
              )}

              {/* Grace-window grayed state — owner-configured `deal_expired_grace_hours`
                  controls how long this stays visible before the popup hides
                  itself entirely (`expires.hidden`). */}
              {isExpiredGrace && (
                <div
                  className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-2"
                  data-testid="exit-intent-expired-banner"
                >
                  <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>This offer ended on <strong>{expires.display}</strong>. Check back soon.</span>
                </div>
              )}

              {/* Countdown */}
              {config.countdown_enabled && countdown > 0 && (
                <div className="flex items-center justify-center gap-2 mb-4 p-2 bg-amber-50 rounded-lg" data-testid="exit-intent-countdown">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">
                    Offer expires in {formatCountdown(countdown)}
                  </span>
                </div>
              )}
              {config.countdown_enabled && countdown === 0 && tracked && (
                <div className="flex items-center justify-center gap-2 mb-4 p-2 bg-red-50 rounded-lg">
                  <Clock className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-semibold text-red-600">Offer expired</span>
                </div>
              )}

              {/* The deal code is intentionally HIDDEN until consent is
                  granted and the SMS is sent — this matches the lead-form
                  surface (single shared code, single delivery path). The
                  customer sees a teaser tag with no code visible. */}
              <div
                className="flex items-center justify-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border-2 border-dashed"
                style={{ borderColor: `${accentColor}40` }}
                data-testid="exit-intent-coupon-hidden"
              >
                <Tag className="w-4 h-4" style={{ color: accentColor }} />
                <span className="font-mono text-sm font-semibold text-gray-700 tracking-wider">
                  Code unlocks via text
                </span>
              </div>

              {/* Form */}
              <div className="space-y-3 mb-4">
                <Input
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  className="h-11"
                  data-testid="exit-intent-name"
                />
                <Input
                  type="tel"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                  className="h-11"
                  data-testid="exit-intent-phone"
                />
                <Input
                  type="email"
                  placeholder="Email address (optional)"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                  className="h-11"
                  data-testid="exit-intent-email"
                />
              </div>

              {/* Consent — un-checked by default, TCPA-compliant. Single
                  combined checkbox covers both deal-code delivery and
                  future marketing per Phase 1.4. */}
              <div className="space-y-2 mb-4 text-xs text-gray-700">
                <label className="flex gap-2 items-start cursor-pointer" data-testid="exit-intent-consent-row">
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-0.5"
                    data-testid="exit-intent-consent-sms"
                  />
                  <span>
                    I agree to receive my deal code and future messages from
                    Upscale Nails &amp; Spa via text or email. Msg &amp; data
                    rates may apply. Reply <strong>STOP</strong> at any time
                    to opt out.
                  </span>
                </label>
              </div>

              <Button
                ref={ctaBtnRef}
                onClick={handleSubmit}
                disabled={submitting || isExpiredGrace || (config.countdown_enabled && countdown === 0 && tracked)}
                className="w-full h-12 text-base font-semibold rounded-xl text-white gap-2 transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}
                data-testid="exit-intent-cta"
              >
                {isExpiredGrace ? "Offer ended" : submitting ? "Sending…" : (config.cta_text || "Text Me My Deal Code")}
                {!isExpiredGrace && <ArrowRight className="w-4 h-4" />}
              </Button>

              <button
                onClick={handleDismiss}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3 transition-colors"
                data-testid="exit-intent-no-thanks"
              >
                No thanks, I'll pay full price
              </button>
            </>
          )}
        </div>
      </div>
      {showTerms && termsMd && (
        <div
          className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          onClick={() => setShowTerms(false)}
          data-testid="exit-intent-terms-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-gray-900">Terms &amp; Conditions</h3>
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="rounded-full hover:bg-gray-100 p-1 transition-colors"
                aria-label="Close terms"
                data-testid="exit-intent-terms-close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div
              className="px-6 py-5 overflow-y-auto prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(termsMd) }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
